import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import './TaskChecklist.css';

/**
 * Editable checklist for task creation/editing.
 * @param {string[]} items - Array of checklist items
 * @param {function} onChange - Called with new array when items change
 * @param {string} placeholder - Placeholder for new item input
 * @param {string} label - Section label
 */
const TaskChecklist = ({ items = [], onChange, placeholder = 'Add what needs to be done...', label = 'What needs to be done' }) => {
  const [newItem, setNewItem] = useState('');

  const handleAdd = (e) => {
    e.preventDefault();
    const trimmed = newItem.trim();
    if (!trimmed) return;
    const updated = [...(items || []), trimmed];
    onChange(updated);
    setNewItem('');
  };

  const handleRemove = (index) => {
    const updated = [...(items || [])];
    updated.splice(index, 1);
    onChange(updated);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd(e);
    }
  };

  return (
    <div className="task-checklist">
      <label className="task-checklist-label">{label}</label>
      <div className="task-checklist-input-row">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="task-checklist-input"
        />
        <button type="button" onClick={handleAdd} className="task-checklist-add-btn" disabled={!newItem.trim()}>
          + Add
        </button>
      </div>
      {items && items.length > 0 && (
        <ul className="task-checklist-list">
          {items.map((item, index) => (
            <li key={index} className="task-checklist-item">
              <span className="task-checklist-check">✓</span>
              <span className="task-checklist-text">{item}</span>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="task-checklist-remove"
                aria-label="Remove"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Read-only display of checklist items. When completed array is provided (for completed tasks), shows ✓ or ✗ per item. */
export const ChecklistDisplay = ({ items = [], completed = [] }) => {
  const { t } = useLanguage();
  if (!items || items.length === 0) return null;
  const hasCompletionData = completed && completed.length === items.length;
  return (
    <div className="task-checklist-display">
      <strong>{hasCompletionData ? t('cleaner.whatWasDone', 'What was done') : t('client.whatNeedsToBeDone', 'What needs to be done')}:</strong>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 0 0' }}>
        {items.map((item, index) => {
          const isDone = hasCompletionData ? completed[index] : false;
          return (
            <li key={index} className={`task-checklist-display-item ${hasCompletionData ? (isDone ? 'completed' : 'not-completed') : ''}`}>
              <span className="task-checklist-display-check">{hasCompletionData ? (isDone ? '✓' : '✗') : '✓'}</span>
              <span>{item}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/**
 * Checklist with checkboxes for cleaner to mark completion when completing a task.
 */
export const ChecklistCompletion = ({ items = [], completed = [], onChange }) => {
  const { t } = useLanguage();
  if (!items || items.length === 0) return null;
  const handleToggle = (index) => {
    const updated = [...completed];
    while (updated.length < items.length) updated.push(false);
    updated[index] = !updated[index];
    onChange(updated);
  };
  const completedArr = [...completed];
  while (completedArr.length < items.length) completedArr.push(false);
  return (
    <div className="task-checklist-completion">
      <label className="task-checklist-label">{t('cleaner.markWhatYouCompleted', 'Mark what you completed')}:</label>
      <ul className="task-checklist-completion-list">
        {items.map((item, index) => (
          <li key={index} className="task-checklist-completion-item">
            <label>
              <input
                type="checkbox"
                checked={completedArr[index] || false}
                onChange={() => handleToggle(index)}
              />
              <span>{item}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default TaskChecklist;
